import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import aiService from '../../../services/aiService';
import RateLimitIndicator from '../../../components/ui/RateLimitIndicator';

export default function AgentInterface({ agent, onClose, onComplete, onBack, onNext, isFirst, isLast }) {
  const [formData, setFormData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    setIsProcessing(true);
    setError('');
    
    try {
      let content = '';
      
      // Call appropriate AI service based on agent ID
      switch (agent?.id) {
        case 1: // Topic Selection & Research
          content = await aiService?.generateTopicRecommendations({
            industry: formData?.industry,
            audience: formData?.audience,
            goal: formData?.goal,
          });
          break;
          
        case 2: // Writing Voice Capture
          // In real implementation, this would analyze uploaded file
          content = await aiService?.analyzeWritingVoice(
            formData?.writingSample || 'Sample writing to be replaced with actual uploaded content'
          );
          break;
          
        case 3: // Outline Creation
          content = await aiService?.generateBookOutline({
            title: formData?.title,
            wordCount: formData?.wordCount,
            chapters: formData?.chapters,
            topics: formData?.topics,
          });
          break;
          
        case 6: // Chapter-by-Chapter Writing
          content = await aiService?.generateChapter(
            {
              chapterNumber: 1,
              title: 'Understanding the AI Revolution',
              wordCount: '4500',
              keyPoints: 'Current state of AI, misconceptions, real-world examples, integration roadmap',
            },
            'Professional yet conversational tone with actionable insights',
            'Book about AI-powered entrepreneurship'
          );
          break;
          
        case 9: // Cover Design
          content = await aiService?.generateCoverConcepts({
            title: formData?.title || 'Your Book Title',
            subtitle: formData?.subtitle,
            coverStyle: formData?.coverStyle,
            colorScheme: formData?.colorScheme,
            authorPhoto: formData?.authorPhoto,
            category: 'Business & Entrepreneurship',
          });
          break;
          
        case 10: // KDP Metadata
          content = await aiService?.generateKDPMetadata({
            title: formData?.title || 'Your Book Title',
            description: formData?.description,
            category: formData?.category,
            keywords: formData?.keywords,
          });
          break;
          
        case 11: // Publishing Guidance
          content = await aiService?.generatePublishingGuidance();
          break;
          
        default:
          content = 'AI generation for this agent coming soon. Using OpenAI GPT-5 for content generation.';
      }
      
      setGeneratedContent(content);
    } catch (err) {
      console.error('AI generation error:', err);
      setError(err?.message || 'Failed to generate content. Please check your OpenAI API key in the .env file and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderAgentContent = () => {
    switch (agent?.id) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Book Topic Research
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Let's analyze Amazon's Best Seller Rank data to find high-potential topics in your niche.
              </p>
            </div>
            <Input
              label="Your Industry/Niche"
              type="text"
              placeholder="e.g., Digital Marketing, Real Estate, Coaching"
              value={formData?.industry || ''}
              onChange={(e) => handleInputChange('industry', e?.target?.value)}
              description="What field do you want to establish authority in?"
            />
            <Input
              label="Target Audience"
              type="text"
              placeholder="e.g., Small business owners, First-time entrepreneurs"
              value={formData?.audience || ''}
              onChange={(e) => handleInputChange('audience', e?.target?.value)}
            />
            <Select
              label="Book Goal"
              options={[
                { value: 'authority', label: 'Build Authority & Credibility' },
                { value: 'leads', label: 'Generate Business Leads' },
                { value: 'speaking', label: 'Unlock Speaking Opportunities' },
                { value: 'teaching', label: 'Create Teaching/Coaching Material' }
              ]}
              value={formData?.goal || ''}
              onChange={(value) => handleInputChange('goal', value)}
              placeholder="Select your primary goal"
            />
            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
              disabled={!formData?.industry || !formData?.audience || !formData?.goal}
            >
              Generate Topic Recommendations
            </Button>
            {error && (
              <div className="mt-4 p-4 bg-destructive/10 border border-destructive rounded-lg">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-destructive mb-1">Error</h4>
                    <p className="text-xs text-destructive/90">{error}</p>
                  </div>
                </div>
              </div>
            )}
            {generatedContent && (
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <div className="flex items-start space-x-2 mb-3">
                  <Icon name="Lightbulb" size={20} color="var(--color-primary)" />
                  <h4 className="text-sm font-semibold text-foreground">AI Recommendations</h4>
                </div>
                <pre className="text-xs text-foreground whitespace-pre-wrap font-mono">
                  {generatedContent}
                </pre>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Capture Your Writing Voice
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Upload a sample of your writing (blog post, article, or document) so AI can learn your unique style.
              </p>
            </div>

            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors duration-200">
              <Icon name="Upload" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground mb-2">
                Drop your PDF here or click to browse
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Supported: PDF up to 10MB
              </p>
              <Button variant="outline" size="sm">
                Choose File
              </Button>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={14} className="flex-shrink-0 mt-0.5" />
                <p>
                  For best results, upload 2-5 pages of your original writing. The AI will analyze your tone, vocabulary, sentence structure, and style patterns.
                </p>
              </div>
            </div>

            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
            >
              Analyze Writing Voice
            </Button>

            {generatedContent && (
              <div className="mt-6 p-4 bg-success/10 border border-success rounded-lg">
                <div className="flex items-start space-x-2 mb-3">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <h4 className="text-sm font-semibold text-foreground">Voice Profile Created</h4>
                </div>
                <pre className="text-xs text-foreground whitespace-pre-wrap">
                  {generatedContent}
                </pre>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Create Book Outline
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Let's structure your book with a detailed table of contents and chapter breakdown.
              </p>
            </div>
            <Input
              label="Book Title"
              type="text"
              placeholder="Enter your chosen book title"
              value={formData?.title || ''}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
            />
            <Input
              label="Target Word Count"
              type="number"
              placeholder="e.g., 40000"
              value={formData?.wordCount || ''}
              onChange={(e) => handleInputChange('wordCount', e?.target?.value)}
              description="Typical business books: 30,000-50,000 words"
            />
            <Select
              label="Number of Chapters"
              options={[
                { value: '8', label: '8 Chapters (Concise)' },
                { value: '10', label: '10 Chapters (Standard)' },
                { value: '12', label: '12 Chapters (Comprehensive)' },
                { value: '15', label: '15 Chapters (In-depth)' }
              ]}
              value={formData?.chapters || ''}
              onChange={(value) => handleInputChange('chapters', value)}
              placeholder="Select chapter count"
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Key Topics to Cover</label>
              <textarea
                className="w-full min-h-32 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="List the main topics, concepts, or lessons you want to include in your book..."
                value={formData?.topics || ''}
                onChange={(e) => handleInputChange('topics', e?.target?.value)}
              />
            </div>
            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
              disabled={!formData?.title || !formData?.wordCount || !formData?.chapters}
            >
              Generate Detailed Outline
            </Button>
            {generatedContent && (
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <div className="flex items-start space-x-2 mb-3">
                  <Icon name="BookOpen" size={20} color="var(--color-primary)" />
                  <h4 className="text-sm font-semibold text-foreground">Your Book Outline</h4>
                </div>
                <pre className="text-xs text-foreground whitespace-pre-wrap">
                  {generatedContent}
                </pre>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Choose Your Writing Path
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Select how you want to create your manuscript.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={() => handleInputChange('path', 'automatic')}
                className={`p-6 rounded-lg border-2 transition-all duration-200 text-left ${
                  formData?.path === 'automatic' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Zap" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">
                      Automatic Generation
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Fast & efficient
                    </p>
                  </div>
                </div>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                    <span>Full manuscript in 2-3 hours</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                    <span>Consistent flow and voice</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                    <span>Best for quick completion</span>
                  </li>
                </ul>
              </button>

              <button
                onClick={() => handleInputChange('path', 'chapter')}
                className={`p-6 rounded-lg border-2 transition-all duration-200 text-left ${
                  formData?.path === 'chapter' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Edit" size={20} color="var(--color-secondary)" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">
                      Chapter-by-Chapter
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Interactive & detailed
                    </p>
                  </div>
                </div>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                    <span>Review each chapter individually</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                    <span>Add personal stories and input</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                    <span>Maximum control and customization</span>
                  </li>
                </ul>
              </button>
            </div>
            {formData?.path && (
              <Button
                variant="default"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={handleGenerate}
              >
                Continue with {formData?.path === 'automatic' ? 'Automatic' : 'Chapter-by-Chapter'}
              </Button>
            )}
            {generatedContent && (
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <pre className="text-xs text-foreground whitespace-pre-wrap">
                  {generatedContent}
                </pre>
              </div>
            )}
          </div>
        );

      case 5:
      case 6:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                {agent?.id === 5 ? 'Automatic Manuscript Generation' : 'Chapter-by-Chapter Writing'}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {agent?.id === 5
                  ? 'AI is generating your complete manuscript based on the outline and your writing voice.'
                  : 'Work through each chapter with AI assistance, adding your personal insights and stories.'}
              </p>
            </div>
            {agent?.id === 5 ? (
              <>
                <div className="p-6 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-foreground">Generation Progress</span>
                    <span className="text-sm font-mono text-muted-foreground">
                      {isProcessing ? '45%' : '0%'}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-400"
                      style={{ width: isProcessing ? '45%' : '0%' }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {isProcessing ? 'Generating Chapter 5 of 10...' : 'Ready to start generation'}
                  </p>
                </div>

                <Button
                  variant="default"
                  fullWidth
                  iconName="Sparkles"
                  iconPosition="left"
                  loading={isProcessing}
                  onClick={handleGenerate}
                >
                  {isProcessing ? 'Generating Manuscript...' : 'Start Generation'}
                </Button>
              </>
            ) : (
              <>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">Chapter 1 of 10</span>
                    <span className="text-xs text-muted-foreground">~4,500 words</span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Understanding the AI Revolution
                  </h4>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Sparkles"
                    iconPosition="left"
                    loading={isProcessing}
                    onClick={handleGenerate}
                  >
                    Generate Chapter Draft
                  </Button>
                </div>

                {generatedContent && (
                  <div className="space-y-4">
                    <div className="p-4 bg-background border border-border rounded-lg max-h-96 overflow-y-auto">
                      <pre className="text-xs text-foreground whitespace-pre-wrap">
                        {generatedContent}
                      </pre>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Your Feedback/Additions</label>
                      <textarea
                        className="w-full min-h-24 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Add personal stories, modify content, or provide feedback..."
                      />
                    </div>

                    <div className="flex space-x-3">
                      <Button variant="outline" fullWidth iconName="RefreshCw" iconPosition="left">
                        Regenerate
                      </Button>
                      <Button variant="default" fullWidth iconName="Check" iconPosition="left">
                        Approve & Continue
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 7:
      case 8:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                {agent?.id === 7 ? 'Print Formatting' : 'Ebook Formatting'}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {agent?.id === 7
                  ? 'Format your manuscript for Amazon KDP print-on-demand.'
                  : 'Optimize your manuscript for Kindle and other ebook platforms.'}
              </p>
            </div>
            {agent?.id === 7 && (
              <Select
                label="Trim Size"
                options={[
                  { value: '6x9', label: '6" x 9" (Standard Trade Paperback)' },
                  { value: '5.5x8.5', label: '5.5" x 8.5" (Digest Size)' },
                  { value: '5.25x8', label: '5.25" x 8" (Compact)' },
                  { value: '8x10', label: '8" x 10" (Large Format)' }
                ]}
                value={formData?.trimSize || ''}
                onChange={(value) => handleInputChange('trimSize', value)}
                placeholder="Select trim size"
                description="6x9 is recommended for business/professional books"
              />
            )}
            <div className="space-y-3">
              <Checkbox
                label="Include table of contents"
                checked={formData?.toc || false}
                onChange={(e) => handleInputChange('toc', e?.target?.checked)}
              />
              <Checkbox
                label="Add page numbers"
                checked={formData?.pageNumbers || false}
                onChange={(e) => handleInputChange('pageNumbers', e?.target?.checked)}
              />
              <Checkbox
                label="Include copyright page"
                checked={formData?.copyright || false}
                onChange={(e) => handleInputChange('copyright', e?.target?.checked)}
              />
              {agent?.id === 7 && (
                <Checkbox
                  label="Add ISBN page"
                  checked={formData?.isbn || false}
                  onChange={(e) => handleInputChange('isbn', e?.target?.checked)}
                />
              )}
            </div>
            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
            >
              Generate {agent?.id === 7 ? 'Print' : 'Ebook'} Format
            </Button>
            {generatedContent && (
              <div className="mt-6 p-4 bg-success/10 border border-success rounded-lg">
                <div className="flex items-start space-x-2 mb-3">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <h4 className="text-sm font-semibold text-foreground">Formatting Complete</h4>
                </div>
                <pre className="text-xs text-foreground whitespace-pre-wrap">
                  {generatedContent}
                </pre>
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left" className="mt-4">
                  Download {agent?.id === 7 ? 'PDF' : 'MOBI/EPUB'}
                </Button>
              </div>
            )}
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Book Cover Design
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Create a professional book cover based on competitor analysis and best practices.
              </p>
            </div>
            <Select
              label="Cover Style Preference"
              options={[
                { value: 'typography', label: 'Bold Typography (Text-focused)' },
                { value: 'symbolic', label: 'Symbolic Imagery (Conceptual)' },
                { value: 'photographic', label: 'Photographic (Lifestyle/Scene)' },
                { value: 'minimalist', label: 'Minimalist (Clean & Simple)' }
              ]}
              value={formData?.coverStyle || ''}
              onChange={(value) => handleInputChange('coverStyle', value)}
              placeholder="Select cover style"
            />
            <Select
              label="Color Scheme"
              options={[
                { value: 'professional', label: 'Professional (Blue/Navy)' },
                { value: 'energetic', label: 'Energetic (Orange/Red)' },
                { value: 'sophisticated', label: 'Sophisticated (Purple/Gold)' },
                { value: 'modern', label: 'Modern (Black/White/Accent)' }
              ]}
              value={formData?.colorScheme || ''}
              onChange={(value) => handleInputChange('colorScheme', value)}
              placeholder="Select color scheme"
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Subtitle (Optional)</label>
              <Input
                type="text"
                placeholder="A practical guide to..."
                value={formData?.subtitle || ''}
                onChange={(e) => handleInputChange('subtitle', e?.target?.value)}
              />
            </div>
            <Checkbox
              label="Include author photo on cover"
              checked={formData?.authorPhoto || false}
              onChange={(e) => handleInputChange('authorPhoto', e?.target?.checked)}
            />
            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
              disabled={!formData?.coverStyle || !formData?.colorScheme}
            >
              Generate Cover Concepts
            </Button>
            {generatedContent && (
              <div className="mt-6 space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <pre className="text-xs text-foreground whitespace-pre-wrap">
                    {generatedContent}
                  </pre>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3]?.map((concept) => (
                    <button
                      key={concept}
                      className="aspect-[2/3] bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center text-white font-heading font-bold text-sm hover:opacity-80 transition-opacity duration-200"
                    >
                      Concept {concept}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 10:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                KDP Metadata Generation
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Generate optimized metadata for Amazon Kindle Direct Publishing.
              </p>
            </div>
            <Input
              label="Book Description"
              type="text"
              placeholder="Compelling description for Amazon listing..."
              value={formData?.description || ''}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              description="This will be refined by AI for maximum appeal"
            />
            <Select
              label="Primary Category"
              options={[
                { value: 'business', label: 'Business & Money' },
                { value: 'selfhelp', label: 'Self-Help' },
                { value: 'technology', label: 'Computers & Technology' },
                { value: 'education', label: 'Education & Teaching' }
              ]}
              value={formData?.category || ''}
              onChange={(value) => handleInputChange('category', value)}
              placeholder="Select primary category"
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Target Keywords</label>
              <textarea
                className="w-full min-h-24 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Enter keywords separated by commas..."
                value={formData?.keywords || ''}
                onChange={(e) => handleInputChange('keywords', e?.target?.value)}
              />
              <p className="text-xs text-muted-foreground">
                AI will optimize these for Amazon's search algorithm
              </p>
            </div>
            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
              disabled={!formData?.description || !formData?.category}
            >
              Generate Optimized Metadata
            </Button>
            {generatedContent && (
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <div className="flex items-start space-x-2 mb-3">
                  <Icon name="Tag" size={20} color="var(--color-primary)" />
                  <h4 className="text-sm font-semibold text-foreground">Optimized Metadata</h4>
                </div>
                <pre className="text-xs text-foreground whitespace-pre-wrap">
                  {generatedContent}
                </pre>
              </div>
            )}
          </div>
        );

      case 11:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Publishing Guidance
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Step-by-step instructions for publishing your book on Amazon KDP.
              </p>
            </div>

            <Button
              variant="default"
              fullWidth
              iconName="Sparkles"
              iconPosition="left"
              loading={isProcessing}
              onClick={handleGenerate}
            >
              Generate Publishing Checklist
            </Button>

            {generatedContent && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <pre className="text-xs text-foreground whitespace-pre-wrap">
                    {generatedContent}
                  </pre>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <Button variant="outline" iconName="ExternalLink" iconPosition="right">
                    Open Amazon KDP
                  </Button>
                  <Button variant="outline" iconName="Users" iconPosition="right">
                    Join Skool Community
                  </Button>
                </div>

                <div className="p-4 bg-success/10 border border-success rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="Trophy" size={20} color="var(--color-success)" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        Congratulations!
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        You've completed all steps. Your book is ready to publish and establish your authority in your industry.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Agent interface coming soon
            </p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon name={agent?.icon} size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-heading font-bold text-foreground">
              {agent?.name}
            </h2>
            <p className="text-sm text-muted-foreground">
              {agent?.description}
            </p>
          </div>
        </div>

        {/* Rate Limit Indicator */}
        <div className="px-6 pt-4">
          <RateLimitIndicator />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderAgentContent()}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <Button
            variant="ghost"
            iconName="ChevronLeft"
            iconPosition="left"
            onClick={onBack}
            disabled={isFirst}
          >
            Previous
          </Button>

          <Button
            variant="default"
            iconName="ChevronRight"
            iconPosition="right"
            onClick={onNext}
            disabled={isLast}
          >
            {isLast ? 'Complete' : 'Next Agent'}
          </Button>
        </div>
      </div>
    </div>
  );
}